// App root — the ONLY place that decides which UI shell renders.
//
//   src/core        shared logic (API, MQTT, state, device maps) — written once
//   src/ui/mobile   Flutter-replica phone UI (Capacitor Android/iOS, narrow web)
//   src/ui/desktop  wide sidebar UI          (desktop browser)
//
// Both shells sit inside the same provider, theme and router, so a session, an
// MQTT connection and a device command behave identically on either.

import React, { useEffect, useMemo, useState } from 'react';
import { HashRouter, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { App as CapacitorApp } from '@capacitor/app';
import { AppProvider, useApp } from './core/state/AppContext';
import { buildTheme } from './core/theme/buildTheme';
import { connectMqtt } from './core/mqtt/mqttClient';
import { useUiMode } from './core/platform';
import { initOfflineWatch } from './core/offline/offlineMode';

import DesktopRoutes from './ui/desktop/DesktopRoutes';
import MobileRoutes from './ui/mobile/MobileRoutes';
import OtaUpdater from './ui/shared/OtaUpdater';
import ExitConfirmDialog from './ui/shared/ExitConfirmDialog';
import ForceUpgradeGate from './ui/shared/ForceUpgradeGate';
import EscapeBack from './ui/shared/EscapeBack';

// Android hardware back: navigate within the SPA instead of letting the webview
// exit the app. (No-op on web — there is no hardware back button.)
//
// Mirrors the Flutter app's WillPopScope chain:
//   • not at a root screen        -> go back one step
//   • on /home but not the first tab -> return to the Favourites tab
//     (Flutter's non-first tabs pop back to HomeScreen rather than exiting)
//   • at a root screen            -> ASK before exiting (Flutter's exitPopup)
//
// Asking matters: without it a single stray tap on the home screen kills the app
// mid-session, which is easy to do one-handed.
const ROOT_PATHS = ['/home', '/login', '/splash', '/blocked'];

function HardwareBack() {
  const navigate = useNavigate();
  const location = useLocation();
  const [askExit, setAskExit] = useState(false);

  useEffect(() => {
    let handle;
    CapacitorApp.addListener('backButton', () => {
      if (!ROOT_PATHS.includes(location.pathname)) { navigate(-1); return; }

      // On the tabbed home screen, a non-first tab returns to the first tab
      // before back means "exit".
      if (location.pathname === '/home') {
        const tab = Number(new URLSearchParams(location.search).get('tab') || 0);
        if (tab !== 0) { navigate('/home?tab=0', { replace: true }); return; }
      }

      setAskExit(true);
    }).then((h) => { handle = h; }).catch(() => {});
    return () => { try { handle && handle.remove(); } catch (e) { /* ignore */ } };
  }, [navigate, location.pathname, location.search]);

  return (
    <ExitConfirmDialog
      open={askExit}
      onCancel={() => setAskExit(false)}
      onExit={() => {
        setAskExit(false);
        CapacitorApp.exitApp().catch(() => {});
      }}
    />
  );
}

function Shell() {
  const { user, isAuthed } = useApp();
  const uiMode = useUiMode();
  const theme = useMemo(() => buildTheme(user), [user]);

  // Watch connectivity from the very start: everything downstream (status
  // badges, control transport, the "no network" hint) reads offlineMode, and it
  // must be right before the first screen asks.
  useEffect(() => { initOfflineWatch(); }, []);

  // Connect MQTT once the user is logged in so live control/status work.
  useEffect(() => {
    if (isAuthed) connectMqtt();
  }, [isAuthed]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Self-hosted OTA live updates. Mounted once, outside the router, so a
          navigation can't remount it and fire a second update check. */}
      {/* TWO update paths, checked on every start, in this order:
         *
         *   1. PLAY STORE  (ForceUpgradeGate) — a newer NATIVE build. Blocking:
         *      the app cannot be used until it is installed, because a native
         *      release is the only way to ship plugin/permission/SDK changes.
         *   2. OTA         (OtaUpdater) — a newer WEB BUNDLE. Silent: downloads
         *      in the background and applies on the next cold start.
         *
         * OtaUpdater is mounted INSIDE the gate on purpose. If Play has a newer
         * native build, downloading an OTA bundle is pointless work: the user is
         * about to install a new APK that carries its own bundle anyway, and the
         * downloaded one could be built against native code this install lacks.
         * Gate first, then OTA. */}
      <ForceUpgradeGate>
        <OtaUpdater />
        <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <HardwareBack />
          {/* Escape = back on every screen. Desktop has no hardware back key,
              so without this a full-window screen can only be left via its own
              arrow. */}
          <EscapeBack />
          {uiMode === 'mobile' ? <MobileRoutes /> : <DesktopRoutes />}
        </HashRouter>
      </ForceUpgradeGate>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AppProvider>
      <Shell />
    </AppProvider>
  );
}
